import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { captureRequest } from '@/lib/analytics/posthog-events';

function axiosActionLabel(config: InternalAxiosRequestConfig): string {
  return config.url || '';
}

// 创建 axios 实例
const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL || '',
  timeout: 30000, // 30秒超时
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// 请求拦截器
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    return config;
  },
  (error: AxiosError) => {
    console.error('❌ 请求拦截器错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    const action = axiosActionLabel(response.config);
    const data = response.data;
    let businessCode: number | undefined;
    let businessOk = true;
    if (data && typeof data === 'object' && 'code' in data && typeof (data as { code: unknown }).code === 'number') {
      businessCode = (data as { code: number }).code;
      businessOk = businessCode === 200;
    }
    const httpOk = response.status >= 200 && response.status < 300;
    const ok = httpOk && businessOk;
    captureRequest({
      action,
      status: ok ? 'success' : 'fail',
      code: businessCode ?? response.status,
      msg:
        !ok && data && typeof data === 'object' && typeof (data as { msg?: unknown }).msg === 'string'
          ? (data as { msg: string }).msg.slice(0, 200)
          : undefined,
    });

    // 处理业务错误码
    if (response.data && typeof response.data === 'object') {
      // OKX API 错误处理
      if (response.data.code && response.data.code !== 200) {
        console.log('response error', response);
        // return Promise.reject(
        //   new Error(response.data.msg || `业务错误: ${response.data.code}`)
        // );
        return response;
      }
      return response.data;
    }
    return response.data;
  },
  (error: AxiosError) => {
    const cfg = error.config;
    const action = cfg ? axiosActionLabel(cfg) : 'unknown';
    const httpStatus = error.response?.status;
    const errData = error.response?.data as { code?: number; msg?: string } | undefined;
    const businessCode = errData && typeof errData.code === 'number' ? errData.code : undefined;
    captureRequest({
      action,
      status: 'fail',
      code: businessCode ?? httpStatus,
      msg: error.message?.slice(0, 200),
    });

    // 处理不同类型的错误
    if (error.response) {
      // 服务器响应了错误状态码
      const status = error.response.status;
      const data = error.response.data as any;

      switch (status) {
        case 400:
          return Promise.reject(new Error(data?.message || '请求参数错误'));
        case 401:
          // 清除无效的认证信息
          if (typeof window !== 'undefined') {
            localStorage.removeItem('auth_token');
          }
          return Promise.reject(new Error('认证失败，请重新登录'));
        case 403:
          return Promise.reject(new Error('权限不足'));
        case 404:
          return Promise.reject(new Error('请求的资源不存在'));
        case 429:
          return Promise.reject(new Error('请求过于频繁，请稍后重试'));
        case 500:
          return Promise.reject(new Error('服务器内部错误'));
        case 502:
          return Promise.reject(new Error('网关错误'));
        case 503:
          return Promise.reject(new Error('服务暂时不可用'));
        case 504:
          return Promise.reject(new Error('网关超时'));
        default:
          return Promise.reject(new Error(data?.message || `请求失败 (${status})`));
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      return Promise.reject(new Error('网络连接失败，请检查网络设置'));
    } else {
      // 请求配置有误
      return Promise.reject(new Error('请求配置错误'));
    }
  }
);

// 重试机制
/*
const retryRequest = async (
  config: AxiosRequestConfig,
  retries: number = 3,
  delay: number = 1000
): Promise<AxiosResponse> => {
  try {
    return await axiosInstance(config)
  } catch (error) {
    if (retries > 0 && axios.isAxiosError(error)) {
      const status = error.response?.status || 0;
      const retryCode = [429, 500, 502, 503, 504];
      // 只对特定错误进行重试
      if (retryCode.includes(status)) {
        console.log(`🔄 重试请求 (${retries} 次剩余):`, config.url)
        // 延迟重试
        await new Promise(resolve => setTimeout(resolve, delay))
        return retryRequest(config, retries - 1, delay * 2)
      }
    }
    throw error
  }
}

// 带重试的请求方法
export const axiosWithRetry = {
  get: (url: string, config?: AxiosRequestConfig) => retryRequest({ ...config, method: 'GET', url }),
  post: (url: string, data?: any, config?: AxiosRequestConfig) => retryRequest({ ...config, method: 'POST', url, data }),
  put: (url: string, data?: any, config?: AxiosRequestConfig) => retryRequest({ ...config, method: 'PUT', url, data }),
  delete: (url: string, config?: AxiosRequestConfig) => retryRequest({ ...config, method: 'DELETE', url }),
  patch: (url: string, data?: any, config?: AxiosRequestConfig) => retryRequest({ ...config, method: 'PATCH', url, data }),
}
*/

// 导出配置好的 axios 实例
export default axiosInstance;

// 导出类型
export type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError };
